import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { SearchIcon, CalendarIcon, DownloadIcon, FilterIcon, StarIcon, ThumbsUpIcon, ThumbsDownIcon, BarChart2Icon, UserIcon, FileTextIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon, MessageSquareIcon } from 'lucide-react';
// Mock data for feedback
const mockFeedback = [{
  id: 1,
  trainingId: 101,
  trainingType: 'Sliptraining Basis',
  date: new Date(new Date().setDate(new Date().getDate() - 5)),
  location: 'Rotterdam',
  instructor: 'Erik de Vries',
  client: 'Gemeente Rotterdam',
  participants: 8,
  rating: 4.8,
  strengths: ['Duidelijke uitleg', 'Praktische oefeningen', 'Goede locatie'],
  improvements: ['Meer tijd voor vragen'],
  comments: [{
    id: 1001,
    text: 'Zeer leerzame training. De instructeur gaf duidelijke uitleg en de praktijkoefeningen waren zeer nuttig.',
    rating: 5,
    participantName: 'Jan Janssen'
  }, {
    id: 1002,
    text: 'Goede training, maar iets te weinig tijd voor vragen aan het einde.',
    rating: 4,
    participantName: 'Piet Pietersen'
  }]
}, {
  id: 2,
  trainingId: 102,
  trainingType: 'Defensief Rijden',
  date: new Date(new Date().setDate(new Date().getDate() - 10)),
  location: 'Utrecht',
  instructor: 'Lisa Bakker',
  client: 'ProRail',
  participants: 6,
  rating: 4.5,
  strengths: ['Goede praktijkvoorbeelden', 'Interactieve training'],
  improvements: ['Meer videomateriaal', 'Langere pauzes'],
  comments: [{
    id: 1003,
    text: 'De praktijkvoorbeelden waren zeer relevant voor onze dagelijkse werkzaamheden.',
    rating: 5,
    participantName: 'Anna Bakker'
  }, {
    id: 1004,
    text: 'Interactieve training, maar zou graag meer videomateriaal zien.',
    rating: 4,
    participantName: 'Peter Post'
  }]
}, {
  id: 3,
  trainingId: 103,
  trainingType: 'Code 95 Training',
  date: new Date(new Date().setDate(new Date().getDate() - 15)),
  location: 'Amsterdam',
  instructor: 'Peter Jansen',
  client: 'Transport BV',
  participants: 12,
  rating: 3.8,
  strengths: ['Relevante inhoud', 'Goede documentatie'],
  improvements: ['Meer praktijkoefeningen', 'Betere timing', 'Modernere faciliteiten'],
  comments: [{
    id: 1005,
    text: 'Inhoudelijk goede training, maar te weinig praktijkoefeningen.',
    rating: 3,
    participantName: 'Hans Hansen'
  }, {
    id: 1006,
    text: 'De documentatie was zeer nuttig en goed voorbereid.',
    rating: 4,
    participantName: 'Petra Peters'
  }]
}, {
  id: 4,
  trainingId: 104,
  trainingType: 'Sliptraining Gevorderd',
  date: new Date(new Date().setDate(new Date().getDate() - 20)),
  location: 'Rotterdam',
  instructor: 'Erik de Vries',
  client: 'Shell Nederland',
  participants: 8,
  rating: 4.9,
  strengths: ['Uitstekende instructeur', 'Uitdagende oefeningen', 'Goede opbouw'],
  improvements: [],
  comments: [{
    id: 1007,
    text: 'Uitstekende training! De instructeur wist precies hoe hij de oefeningen moest aanpassen aan ons niveau.',
    rating: 5,
    participantName: 'Sander de Boer'
  }, {
    id: 1008,
    text: "De opbouw van de training was zeer goed, van basisvaardigheden naar complexere scenario's.",
    rating: 5,
    participantName: 'Laura Visser'
  }]
}, {
  id: 5,
  trainingId: 105,
  trainingType: 'Defensief Rijden',
  date: new Date(new Date().setDate(new Date().getDate() - 25)),
  location: 'Utrecht',
  instructor: 'Maria Visser',
  client: 'Gemeente Utrecht',
  participants: 10,
  rating: 4.2,
  strengths: ['Goede theorie', 'Persoonlijke aandacht'],
  improvements: ['Meer praktijktijd', 'Betere locatie'],
  comments: [{
    id: 1009,
    text: 'De theoretische uitleg was zeer goed, maar ik had graag meer tijd gehad om te oefenen.',
    rating: 4,
    participantName: 'Thomas Bakker'
  }, {
    id: 1010,
    text: 'De instructeur gaf goede persoonlijke feedback, maar de locatie was niet ideaal.',
    rating: 4,
    participantName: 'Sophie Jansen'
  }]
}];
// Mock data for instructors
const mockInstructors = [{
  id: 1,
  name: 'Erik de Vries',
  avgRating: 4.85,
  trainingCount: 45,
  specialties: ['Sliptraining', 'Defensief Rijden'],
  feedbackCount: 2
}, {
  id: 2,
  name: 'Lisa Bakker',
  avgRating: 4.5,
  trainingCount: 32,
  specialties: ['Defensief Rijden', 'Code 95'],
  feedbackCount: 1
}, {
  id: 3,
  name: 'Peter Jansen',
  avgRating: 3.8,
  trainingCount: 28,
  specialties: ['Code 95', 'Calamiteitentraining'],
  feedbackCount: 1
}, {
  id: 4,
  name: 'Maria Visser',
  avgRating: 4.2,
  trainingCount: 22,
  specialties: ['Defensief Rijden', 'Sliptraining'],
  feedbackCount: 1
}];
// Helper function to format date
const formatDate = date => {
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};
export function Feedback() {
  const [feedback, setFeedback] = useState(mockFeedback);
  const [expandedFeedback, setExpandedFeedback] = useState<number | null>(null);
  const [filterInstructor, setFilterInstructor] = useState<string>('all');
  const [filterTrainingType, setFilterTrainingType] = useState<string>('all');
  const [filterRating, setFilterRating] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  // Get all training types from feedback
  const trainingTypes = Array.from(new Set(feedback.map(f => f.trainingType)));
  // Filter and sort feedback
  const filteredAndSortedFeedback = [...feedback].filter(item => {
    // Filter by instructor
    if (filterInstructor !== 'all' && item.instructor !== filterInstructor) {
      return false;
    }
    // Filter by training type
    if (filterTrainingType !== 'all' && item.trainingType !== filterTrainingType) {
      return false;
    }
    // Filter by rating
    if (filterRating !== 'all') {
      const minRating = parseFloat(filterRating);
      if (item.rating < minRating) {
        return false;
      }
    }
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return item.trainingType.toLowerCase().includes(searchLower) || item.instructor.toLowerCase().includes(searchLower) || item.client.toLowerCase().includes(searchLower) || item.location.toLowerCase().includes(searchLower) || item.comments.some(comment => comment.text.toLowerCase().includes(searchLower) || comment.participantName.toLowerCase().includes(searchLower));
    }
    return true;
  }).sort((a, b) => {
    // Sort by selected field
    if (sortBy === 'date') {
      return sortOrder === 'asc' ? new Date(a.date).getTime() - new Date(b.date).getTime() : new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortBy === 'rating') {
      return sortOrder === 'asc' ? a.rating - b.rating : b.rating - a.rating;
    } else if (sortBy === 'trainingType') {
      return sortOrder === 'asc' ? a.trainingType.localeCompare(b.trainingType) : b.trainingType.localeCompare(a.trainingType);
    } else if (sortBy === 'instructor') {
      return sortOrder === 'asc' ? a.instructor.localeCompare(b.instructor) : b.instructor.localeCompare(a.instructor);
    }
    return 0;
  });
  // Toggle feedback details
  const toggleFeedbackDetails = feedbackId => {
    if (expandedFeedback === feedbackId) {
      setExpandedFeedback(null);
    } else {
      setExpandedFeedback(feedbackId);
    }
  };
  // Toggle sort order
  const toggleSort = field => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  // Export feedback data
  const exportFeedbackData = () => {
    // In a real app, this would generate a CSV or Excel file
    alert('In een echte applicatie zou dit een CSV of Excel bestand downloaden met feedback gegevens.');
  };
  // Render stars for rating
  const renderStars = rating => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    return <div className="flex" data-id="element-2075">
        {[...Array(fullStars)].map((_, i) => <StarIcon key={`full-${i}`} className="h-4 w-4 text-yellow-400" data-id="element-2076" />)}
        {halfStar && <StarIcon className="h-4 w-4 text-yellow-400" data-id="element-2077" />}
        {[...Array(emptyStars)].map((_, i) => <StarIcon key={`empty-${i}`} className="h-4 w-4 text-gray-300" data-id="element-2078" />)}
      </div>;
  };
  return <div className="space-y-6" data-id="element-2079">
      <div className="flex justify-between items-center" data-id="element-2080">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2081">
          Feedback & Kwaliteit
        </h1>
        <Button variant="outline" size="sm" onClick={exportFeedbackData} data-id="element-2082">
          <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2083" />
          Exporteren
        </Button>
      </div>
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2084">
        <Card className="col-span-1 p-4" data-id="element-2085">
          <div className="flex items-center" data-id="element-2086">
            <div className="p-2 rounded-full bg-yellow-100" data-id="element-2087">
              <StarIcon className="h-5 w-5 text-yellow-600" data-id="element-2088" />
            </div>
            <div className="ml-3" data-id="element-2089">
              <div className="text-sm text-gray-500" data-id="element-2090">
                Gemiddelde Beoordeling
              </div>
              <div className="text-xl font-bold" data-id="element-2091">
                {(feedback.reduce((sum, item) => sum + item.rating, 0) / feedback.length).toFixed(1)}
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2092">
          <div className="flex items-center" data-id="element-2093">
            <div className="p-2 rounded-full bg-blue-100" data-id="element-2094">
              <MessageSquareIcon className="h-5 w-5 text-blue-600" data-id="element-2095" />
            </div>
            <div className="ml-3" data-id="element-2096">
              <div className="text-sm text-gray-500" data-id="element-2097">Totaal Feedback</div>
              <div className="text-xl font-bold" data-id="element-2098">{feedback.length}</div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2099">
          <div className="flex items-center" data-id="element-2100">
            <div className="p-2 rounded-full bg-green-100" data-id="element-2101">
              <ThumbsUpIcon className="h-5 w-5 text-green-600" data-id="element-2102" />
            </div>
            <div className="ml-3" data-id="element-2103">
              <div className="text-sm text-gray-500" data-id="element-2104">Positieve Feedback</div>
              <div className="text-xl font-bold" data-id="element-2105">
                {feedback.filter(f => f.rating >= 4).length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2106">
          <div className="flex items-center" data-id="element-2107">
            <div className="p-2 rounded-full bg-red-100" data-id="element-2108">
              <ThumbsDownIcon className="h-5 w-5 text-red-600" data-id="element-2109" />
            </div>
            <div className="ml-3" data-id="element-2110">
              <div className="text-sm text-gray-500" data-id="element-2111">Verbeterpunten</div>
              <div className="text-xl font-bold" data-id="element-2112">
                {feedback.reduce((sum, f) => sum + f.improvements.length, 0)}
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2113">
        <div className="col-span-1" data-id="element-2114">
          <div className="relative" data-id="element-2115">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-2116" />
            <input type="text" placeholder="Zoeken..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2117" />
          </div>
        </div>
        <div className="col-span-1" data-id="element-2118">
          <select value={filterInstructor} onChange={e => setFilterInstructor(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-2119">
            <option value="all" data-id="element-2120">Alle instructeurs</option>
            {mockInstructors.map(instructor => <option key={instructor.id} value={instructor.name} data-id="element-2121">
                {instructor.name}
              </option>)}
          </select>
        </div>
        <div className="col-span-1" data-id="element-2122">
          <select value={filterTrainingType} onChange={e => setFilterTrainingType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-2123">
            <option value="all" data-id="element-2124">Alle trainingstypes</option>
            {trainingTypes.map((type, index) => <option key={index} value={type} data-id="element-2125">
                {type}
              </option>)}
          </select>
        </div>
        <div className="col-span-1" data-id="element-2126">
          <select value={filterRating} onChange={e => setFilterRating(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-2127">
            <option value="all" data-id="element-2128">Alle beoordelingen</option>
            <option value="5" data-id="element-2129">5 sterren</option>
            <option value="4" data-id="element-2130">4+ sterren</option>
            <option value="3" data-id="element-2131">3+ sterren</option>
            <option value="2" data-id="element-2132">2+ sterren</option>
            <option value="1" data-id="element-2133">1+ ster</option>
          </select>
        </div>
      </div>
      {/* Instructor ratings */}
      <Card data-id="element-2134">
        <div className="p-4 border-b border-gray-200" data-id="element-2135">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-2136">
            Instructeur Beoordelingen
          </h2>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2137">
          {mockInstructors.map(instructor => <div key={instructor.id} className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow" data-id="element-2138">
              <div className="flex items-center mb-2" data-id="element-2139">
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-2140">
                  <UserIcon className="h-5 w-5 text-gray-600" data-id="element-2141" />
                </div>
                <div className="ml-3" data-id="element-2142">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-2143">
                    {instructor.name}
                  </h3>
                  <div className="flex items-center" data-id="element-2144">
                    {renderStars(instructor.avgRating)}
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2145">
                      {instructor.avgRating.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500" data-id="element-2146">
                <div data-id="element-2147">
                  <span className="font-medium" data-id="element-2148">Trainingen:</span>{' '}
                  {instructor.trainingCount}
                </div>
                <div data-id="element-2149">
                  <span className="font-medium" data-id="element-2150">Feedback:</span>{' '}
                  {instructor.feedbackCount}
                </div>
                <div className="col-span-2" data-id="element-2151">
                  <span className="font-medium" data-id="element-2152">Specialisaties:</span>{' '}
                  {instructor.specialties.join(', ')}
                </div>
              </div>
            </div>)}
        </div>
      </Card>
      {/* Feedback list */}
      <Card data-id="element-2153">
        <div className="p-4 border-b border-gray-200" data-id="element-2154">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-2155">
            Feedback Overzicht
          </h2>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-2156">
          {filteredAndSortedFeedback.length > 0 ? filteredAndSortedFeedback.map(item => <div key={item.id} className="p-4" data-id="element-2157">
                <div className="flex justify-between items-start cursor-pointer" onClick={() => toggleFeedbackDetails(item.id)} data-id="element-2158">
                  <div data-id="element-2159">
                    <div className="flex items-center space-x-2" data-id="element-2160">
                      <Badge variant="info" data-id="element-2161">{item.trainingType}</Badge>
                      <span className="text-sm text-gray-500" data-id="element-2162">
                        {item.client}
                      </span>
                    </div>
                    <h3 className="mt-1 text-sm font-medium text-gray-900" data-id="element-2163">
                      Instructeur: {item.instructor}
                    </h3>
                    <div className="flex items-center mt-1" data-id="element-2164">
                      {renderStars(item.rating)}
                      <span className="ml-1 text-sm font-medium" data-id="element-2165">
                        {item.rating.toFixed(1)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-2166">
                      <CalendarIcon className="inline-block h-3 w-3 mr-1" data-id="element-2167" />
                      {formatDate(item.date)} |
                      <MapPinIcon className="inline-block h-3 w-3 mx-1" data-id="element-2168" />
                      {item.location} |
                      <UserIcon className="inline-block h-3 w-3 mx-1" data-id="element-2169" />
                      {item.participants} deelnemers
                    </p>
                  </div>
                  <div className="flex items-center" data-id="element-2170">
                    <button className="text-gray-400 hover:text-gray-500" onClick={e => {
                e.stopPropagation();
                toggleFeedbackDetails(item.id);
              }} data-id="element-2171">
                      {expandedFeedback === item.id ? <ChevronUpIcon className="h-5 w-5" data-id="element-2172" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-2173" />}
                    </button>
                  </div>
                </div>
                {/* Expanded details */}
                {expandedFeedback === item.id && <div className="mt-4 pl-4 border-l-2 border-gray-200" data-id="element-2174">
                    <div className="space-y-4" data-id="element-2175">
                      {/* Strengths and improvements */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2176">
                        <div data-id="element-2177">
                          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2178">
                            Sterke Punten
                          </h4>
                          <div className="flex flex-wrap gap-2" data-id="element-2179">
                            {item.strengths.map((strength, index) => <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2180">
                                <ThumbsUpIcon className="h-3 w-3 mr-1" data-id="element-2181" />
                                {strength}
                              </span>)}
                          </div>
                        </div>
                        <div data-id="element-2182">
                          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2183">
                            Verbeterpunten
                          </h4>
                          <div className="flex flex-wrap gap-2" data-id="element-2184">
                            {item.improvements.length > 0 ? item.improvements.map((improvement, index) => <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2185">
                                  <ThumbsDownIcon className="h-3 w-3 mr-1" data-id="element-2186" />
                                  {improvement}
                                </span>) : <span className="text-sm text-gray-500" data-id="element-2187">
                                Geen verbeterpunten gemeld
                              </span>}
                          </div>
                        </div>
                      </div>
                      {/* Comments */}
                      <div data-id="element-2188">
                        <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2189">
                          Deelnemer Opmerkingen
                        </h4>
                        <div className="space-y-3" data-id="element-2190">
                          {item.comments.map(comment => <div key={comment.id} className="bg-gray-50 p-3 rounded-md" data-id="element-2191">
                              <div className="flex justify-between items-start" data-id="element-2192">
                                <p className="text-sm text-gray-600" data-id="element-2193">
                                  {comment.text}
                                </p>
                                <div className="flex items-center" data-id="element-2194">
                                  {renderStars(comment.rating)}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-2195">
                                - {comment.participantName}
                              </p>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>) : <div className="p-6 text-center" data-id="element-2196">
              <MessageSquareIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-2197" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2198">
                Geen feedback gevonden
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2199">
                Er is geen feedback die voldoet aan je zoekcriteria
              </p>
            </div>}
        </div>
      </Card>
    </div>;
}